import { createSelector } from 'reselect'
import getCheckoutPointsCalculationRequests from 'checkout/selectors/loyalty/pointsCalculation/getCheckoutPointsCalculationRequests'
import buildPointsKey from 'loyalty/lib/pointsCalculation/buildPointsKey'
import getAllItemViews from 'checkout/selectors/view/getAllItemViews'
import getCheckoutLoyaltyPointsEarnItemTotals from 'checkout/selectors/loyalty/pointsCalculation/getCheckoutLoyaltyPointsEarnItemTotals'
import getShouldEarnLoyaltyPoints from 'checkout/selectors/loyalty/getShouldEarnLoyaltyPoints'
import { getCalculationRequestAfterCommissionIfAgentHubEnabled } from 'lib/agentHub/agentHubUtils'
import { getAgentHubCommissionMap } from 'agentHub/selectors/agentHubSelectors'

const getCheckoutLoyaltyPointsEarnCalculationView = createSelector(
  (state: App.State) => state.loyalty.pointsEarnCalculations,
  (state: App.State) => state.auth.account.memberId,
  (state: App.State) => getCheckoutPointsCalculationRequests(state),
  (state: App.State) => getAllItemViews(state),
  (state: App.State) => getCheckoutLoyaltyPointsEarnItemTotals(state),
  (state: App.State) => getShouldEarnLoyaltyPoints(state),
  (state: App.State) => getAgentHubCommissionMap(state),
  (
    pointsEarnCalculations,
    memberId,
    calculationRequests,
    allItemViews,
    itemsEarnTotals,
    shouldEarnLoyaltyPoints,
    agentHubCommissionMap,
  ): App.WithDataStatus<App.Checkout.LoyaltyPointsEarnCalculationView | undefined> => {
    const earnableItems: Array<App.Checkout.LoyaltyPointsEarnItem> = []

    calculationRequests.forEach((request) => {
      const newCalculationEarnRequest = getCalculationRequestAfterCommissionIfAgentHubEnabled(request, agentHubCommissionMap)
      const pointsKey = buildPointsKey(newCalculationEarnRequest, memberId ?? '')
      const pointsEarnCalculation = pointsEarnCalculations[pointsKey]
      const itemView = allItemViews.data.get(request.itemId ?? '')?.data
      const loyaltyProductType = itemView?.loyalty.productType

      if (pointsEarnCalculation?.data && itemView && loyaltyProductType) {
        const totals = itemsEarnTotals.get(itemView.item.itemId)

        if (!totals) return

        // Get orderItemId for post-purchase flows (e.g., BNBL select-date)
        const orderItemId = 'orderItemId' in itemView.item ? itemView.item.orderItemId : undefined

        const isTourLoyaltyProductType =
          loyaltyProductType === 'le_tour'
          || loyaltyProductType === 'tour_bank'
          || loyaltyProductType === 'trusted_partner_tour'

        earnableItems.push({
          itemId: itemView.item.itemId,
          orderItemId,
          itemTransactionKey: itemView.item.transactionKey,
          itemType: itemView.item.itemType,
          loyaltyProductType,
          title: itemView.title,
          totals,
          pointsEarnCalculation,
          loyaltyGroupKey: itemView.loyalty.groupKey
            ?? (isTourLoyaltyProductType ? request.packageId : undefined),
        })
      }
    })

    const allCalculationsFetched = earnableItems.every(item => !item.pointsEarnCalculation.fetching)

    if (!allCalculationsFetched) {
      return {
        hasRequiredData: false,
        data: undefined,
      }
    }

    return {
      hasRequiredData: true,
      data: {
        shouldEarnLoyaltyPoints,
        earnableItems,
      },
    }
  },
)

export default getCheckoutLoyaltyPointsEarnCalculationView
